import React, { useState } from 'react';
import request from 'ca-request';
import csrfToken from 'ca-environment/csrfToken';
import { InputEditModal, ConfirmationModal } from '@kaizen/draft-modal';
import { Box } from '@kaizen/component-library';
import { Paragraph } from '@kaizen/typography';
import { TextAreaField } from '@kaizen/components';
import notifySavingError from 'ca-capture/helpers/notifySavingError';
import styles from './RatingCommentModal.scss';

type Props = {
  questionId: string;
  questionText: string;
  commentContent: string;
  url: string;
  localeDirection: 'ltr' | 'rtl';
  isOpen: boolean;
  uiText: {
    dialogHeader: string;
    submitLabel: string;
    submittingLabel: string;
    dismissLabel: string;
    repliesInfo: string;
    savingError: string;
    dismissWarningTitle: string;
    dismissWarningBody: string;
    dismissWarningConfirm: string;
    dismissWarningCancel: string;
  };
  dismiss(): void;
  afterSubmit(commentContent: string): void;
  showAdministratorCanReplyToCommentMessage: boolean;
};

function RatingCommentModal(props: Props): React$Element<string> {
  const {
    localeDirection,
    dismiss,
    questionText,
    questionId,
    commentContent,
    url,
    afterSubmit,
    isOpen,
    uiText,
    showAdministratorCanReplyToCommentMessage,
  } = props;

  const [comment, setComment] = useState<string>(commentContent);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [warningOpen, setWarningOpen] = useState<boolean>(false);

  const submitRatingComment = (): void => {
    const data = {
      authenticity_token: csrfToken(),
      comment: comment,
      field: 'comment',
    };

    setSubmitting(true);
    request
      .put(url, {
        body: JSON.stringify(data),
      })
      .then(() => {
        afterSubmit(comment);
        dismiss();
      })
      .catch((err) => {
        notifySavingError(
          err,
          url,
          'heatbar comment request',
          uiText.savingError
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const textareaId = `rating-comment-textbox-${questionId}`;
  const commentChanged = comment !== commentContent;

  const onDismiss = () => {
    if (commentChanged) {
      setWarningOpen(true);
      return;
    }

    dismiss();
  };

  const onWarningConfirm = () => {
    setWarningOpen(false);
    dismiss();
    setComment(commentContent);
  };

  return (
    <>
      <InputEditModal
        isOpen={isOpen}
        mood="positive"
        localeDirection={localeDirection}
        title={uiText.dialogHeader}
        submitLabel={uiText.submitLabel}
        dismissLabel={uiText.dismissLabel}
        onSubmit={submitRatingComment}
        onDismiss={onDismiss}
        submitWorking={
          submitting ? { label: uiText.submittingLabel } : undefined
        }
        automationId="rating-comment-modal"
      >
        {showAdministratorCanReplyToCommentMessage && (
          <Box mb={1}>
            <Paragraph variant="body">{uiText.repliesInfo}</Paragraph>
          </Box>
        )}
        <div className={styles.textareaWrapper}>
          <TextAreaField
            labelText={questionText}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            id={textareaId}
            variant="prominent"
            inline
            rows={4}
          />
        </div>
      </InputEditModal>
      <ConfirmationModal
        isOpen={warningOpen}
        mood="cautionary"
        isProminent
        title={uiText.dismissWarningTitle}
        confirmLabel={uiText.dismissWarningConfirm}
        dismissLabel={uiText.dismissWarningCancel}
        onConfirm={onWarningConfirm}
        onDismiss={() => setWarningOpen(false)}
      >
        <div style={{ textAlign: 'center' }}>
          <Paragraph variant="body">{uiText.dismissWarningBody}</Paragraph>
        </div>
      </ConfirmationModal>
    </>
  );
}

export default RatingCommentModal;
